import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Card, DataTable, Drawer, Segmented, StatusChip, type Column } from '../../components/data';
import { DeleteButton, ErrorBanner, Field, Loading } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { formatCompactCurrency, num, relativeDays, snakeLabel, urgency, type Band } from '../../lib/format';
import { useResource } from '../../lib/hooks';

/**
 * The Admin module's register engine, and the small pieces its ten tabs share.
 *
 * WHY AN ENGINE. Ten registers each need a filterable list, a create form, an
 * edit form and a delete — and the only things that differ between them are a
 * column list, a field list and an endpoint. Written out longhand that is ten
 * near-identical 250-line files, which is ten places for one of those
 * differences to be wrong and no reader able to tell the ten apart. The API side
 * is built the same way and for the same reason; see the note at the top of
 * routes/admin.ts.
 *
 * What is NOT in the engine: every tab's analytics. The charts, the roll-ups and
 * the working lists are what make each register worth having, and they are all
 * different — so they are written out per tab, and the engine is only the plumbing
 * underneath.
 */

/* ── Field specs ────────────────────────────────────────────────────────── */

export type FieldKind =
  | 'text'
  | 'textarea'
  | 'int'
  | 'money'
  | 'percent'
  | 'decimal'
  | 'date'
  | 'select'
  | 'checkbox'
  | 'period';

export interface FieldSpec {
  name: string;
  label: string;
  kind: FieldKind;
  /** For `select`. Rendered in the order given. */
  options?: readonly string[];
  /** Options that are not a lower_snake vocabulary — a picker's rows. */
  choices?: { value: string; label: string }[];
  hint?: string;
  /** Spans both columns of the form grid. */
  wide?: boolean;
  /** A select that allows "nothing chosen". */
  optional?: boolean;
  placeholder?: string;
}

/** A row the engine can render and edit. */
export interface RegisterSpec<T> {
  /** The API path under /admin/registers. */
  resource: string;
  /** Plural, for the heading. */
  title: string;
  /** Singular, for the buttons and the delete confirmation. */
  singular: string;
  columns: Column<T>[];
  fields: FieldSpec[];
  idOf: (row: T) => number;
  labelOf: (row: T) => string;
  /** The row, as the form's fields. Every key must be a field name. */
  toForm: (row: T) => Record<string, unknown>;
  /** A new record's starting values. */
  blank: Record<string, unknown>;
  /** Equality filters the list endpoint accepts, as a segmented control. */
  filters?: { param: string; label: string; options: readonly string[] }[];
  searchHint?: string;
  /** Shown when the register is empty — the chance to say what it is FOR. */
  emptyTitle: string;
  emptyBody: string;
}

/* ── The panel ──────────────────────────────────────────────────────────── */

/**
 * A register: the list, the filters, and the drawer that edits one row.
 *
 * `onChanged` is called after every successful write so the tab's analytics
 * reload alongside the list — otherwise a contract could be added and the
 * renewal pipeline above it would still show the old numbers, which reads as the
 * save not having worked.
 */
export function RegisterPanel<T>({
  spec,
  onChanged,
  canWrite,
}: {
  spec: RegisterSpec<T>;
  onChanged?: () => void;
  canWrite: boolean;
}) {
  /* `term` is what the box shows; `query` is what the API is asked for. Split
     because the search runs server-side as a LIKE across up to eight columns, so
     binding the request to the box directly would be a full scan per keystroke —
     the same reason NocPage.tsx debounces its own search. */
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<{ row: T | null } | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(term), 220);
    return () => clearTimeout(timer);
  }, [term]);

  /* Built as a string so `useResource` re-fetches when it changes, which is the
     hook's whole contract. */
  const path = useMemo(() => {
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
    const qs = params.toString();
    return `/admin/registers/${spec.resource}${qs ? `?${qs}` : ''}`;
  }, [spec.resource, query, filters]);

  const rows = useResource<T[]>(path);

  const reload = () => {
    rows.reload();
    onChanged?.();
  };

  /* A filter or a search being active changes what "empty" means: no rows here
     is a filter that matched nothing, not a register nobody has filled in. */
  const narrowed = Boolean(term.trim()) || Object.values(filters).some(Boolean);

  const columns: Column<T>[] = canWrite
    ? [
        ...spec.columns,
        {
          key: '_edit',
          label: '',
          align: 'right',
          width: '1%',
          render: (row) => (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing({ row })}>
              Edit
            </button>
          ),
        },
      ]
    : spec.columns;

  return (
    <>
      <Card
        title={spec.title}
        subtitle={rows.data ? `${num(rows.data.length)} ${rows.data.length === 1 ? 'record' : 'records'}` : undefined}
        actions={
          canWrite ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing({ row: null })}>
              New {spec.singular.toLowerCase()}
            </button>
          ) : undefined
        }
      >
        <div className="admin-register-filters">
          <div className="field grow">
            <label htmlFor={`${spec.resource}-search`}>Search</label>
            <input
              id={`${spec.resource}-search`}
              className="input"
              type="search"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={spec.searchHint ?? 'Search'}
            />
          </div>
          {spec.filters?.map((filter) => (
            <div className="field" key={filter.param}>
              <label htmlFor={`${spec.resource}-${filter.param}`}>{filter.label}</label>
              <select
                id={`${spec.resource}-${filter.param}`}
                className="select"
                value={filters[filter.param] ?? ''}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, [filter.param]: e.target.value }))
                }
              >
                <option value="">All</option>
                {filter.options.map((o) => (
                  <option key={o} value={o}>
                    {snakeLabel(o)}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        {rows.error && <ErrorBanner message={rows.error} />}
        {rows.loading && !rows.data && <Loading label={`Loading ${spec.title.toLowerCase()}`} />}

        {rows.data && rows.data.length === 0 && !narrowed && (
          <div className="empty">
            <h3>{spec.emptyTitle}</h3>
            <p>{spec.emptyBody}</p>
          </div>
        )}
        {rows.data && rows.data.length === 0 && narrowed && (
          <p className="sub">Nothing matches that search.</p>
        )}
        {rows.data && rows.data.length > 0 && (
          <DataTable columns={columns} rows={rows.data} rowKey={(r) => String(spec.idOf(r))} />
        )}
      </Card>

      {editing && (
        <RecordEditor
          spec={spec}
          row={editing.row}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </>
  );
}

/* ── The editor ─────────────────────────────────────────────────────────── */

/**
 * One record's form, in a drawer.
 *
 * A drawer rather than a Modal because these forms run to eighteen fields — a
 * centred dialog would either scroll inside itself or outgrow the viewport. The
 * Save button is pinned to the panel foot for the same reason.
 */
function RecordEditor<T>({
  spec,
  row,
  onClose,
  onSaved,
}: {
  spec: RegisterSpec<T>;
  row: T | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<Record<string, unknown>>(() =>
    row ? { ...spec.blank, ...spec.toForm(row) } : { ...spec.blank },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (name: string, value: unknown) => setForm((prev) => ({ ...prev, [name]: value }));

  /**
   * Turns an API failure into something that names the field.
   *
   * A duplicate key already comes back as a sentence, so it is used as-is. A zod
   * failure comes back as `{error: 'Validation failed', details: [{path, message}]}`
   * — and showing only the top-level message on an eighteen-field form tells
   * somebody nothing at all, which is exactly what it did before this. The paths
   * are the schema's field names, so they are mapped back to the labels on screen
   * rather than shown raw.
   */
  const describe = (err: unknown): string => {
    if (!(err instanceof ApiError)) return 'Could not save.';

    const details = err.details;
    if (!Array.isArray(details) || details.length === 0) return err.message;

    const labelFor = (path: string) =>
      spec.fields.find((f) => f.name === path)?.label ?? path;

    return details
      .map((d) => {
        const item = d as { path?: unknown; message?: unknown };
        const path = typeof item.path === 'string' ? item.path : '';
        const message = typeof item.message === 'string' ? item.message : 'is not valid';
        return path ? `${labelFor(path)}: ${message}` : message;
      })
      .join(' · ');
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (row) await api.put(`/admin/registers/${spec.resource}/${spec.idOf(row)}`, form);
      else await api.post(`/admin/registers/${spec.resource}`, form);
      onSaved();
    } catch (err) {
      setError(describe(err));
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!row) return;
    setSaving(true);
    setError(null);
    try {
      await api.del(`/admin/registers/${spec.resource}/${spec.idOf(row)}`);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete.');
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      wide
      title={row ? spec.labelOf(row) : `New ${spec.singular.toLowerCase()}`}
      subtitle={row ? spec.singular : undefined}
      footer={
        <div className="row-between wrap">
          <div>
            {row && <DeleteButton what={spec.labelOf(row)} onConfirm={remove} />}
          </div>
          <div className="row wrap">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      }
    >
      {error && <ErrorBanner message={error} />}
      <div className="admin-form-grid">
        {spec.fields.map((field) => (
          <div key={field.name} className={field.wide ? 'admin-form-wide' : undefined}>
            <FieldInput field={field} value={form[field.name]} onChange={(v) => set(field.name, v)} />
          </div>
        ))}
      </div>
    </Drawer>
  );
}

/**
 * One input, by kind.
 *
 * Numbers and dates are kept as STRINGS in form state and sent as strings. The
 * API's zod schemas coerce, and every one of them accepts '' as "not set" — so a
 * cleared field arrives as null rather than as NaN or as the number 0, which is
 * the bug this shape exists to avoid. See the primitives at the top of
 * routes/admin.ts.
 */
function FieldInput({
  field,
  value,
  onChange,
}: {
  field: FieldSpec;
  value: unknown;
  onChange: (next: unknown) => void;
}) {
  const asString = value === null || value === undefined ? '' : String(value);

  if (field.kind === 'checkbox') {
    return (
      <label className="admin-check">
        <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
        <span>{field.label}</span>
        {field.hint && <span className="xs subtle">{field.hint}</span>}
      </label>
    );
  }

  if (field.kind === 'select') {
    const choices =
      field.choices ?? (field.options ?? []).map((o) => ({ value: o, label: snakeLabel(o) }));
    return (
      <Field label={field.label} hint={field.hint}>
        <select className="select" value={asString} onChange={(e) => onChange(e.target.value)}>
          {field.optional && <option value="">{field.placeholder ?? 'None'}</option>}
          {choices.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  if (field.kind === 'textarea') {
    return (
      <Field label={field.label} hint={field.hint}>
        <textarea
          className="input"
          rows={4}
          value={asString}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      </Field>
    );
  }

  const type =
    field.kind === 'date' ? 'date'
    : field.kind === 'period' ? 'month'
    : field.kind === 'text' ? 'text'
    : 'number';

  /* Step matters: a money field stepping by 1 makes the browser reject 1249.50
     on some locales, and a percentage stepping by 1 cannot express 99.5%. */
  const step =
    field.kind === 'money' || field.kind === 'percent' || field.kind === 'decimal' ? '0.01' : '1';

  return (
    <Field label={field.label} hint={field.hint}>
      <input
        className="input"
        type={type}
        step={type === 'number' ? step : undefined}
        value={asString}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
      />
    </Field>
  );
}

/* ── Shared cells and pieces ────────────────────────────────────────────── */

/**
 * A date with how far away it is, on the shared urgency scale.
 *
 * `days` comes from the SERVER — every register's SELECT computes it against
 * `core.BusinessToday()`. Recomputing it here from the date string would put
 * "overdue" at the mercy of the reader's system clock, which is the bug this
 * whole convention exists to prevent.
 */
export function DueCell({ date, days }: { date: string | null; days: number | null }) {
  if (!date) return <span className="sub">—</span>;
  return (
    <span className="admin-due">
      <span>{formatIsoShort(date)}</span>
      <StatusChip band={urgency(days)}>{relativeDays(days)}</StatusChip>
    </span>
  );
}

/** "12 Mar 2026" from a YYYY-MM-DD, without the timezone shift a naive
 *  `new Date()` introduces west of Greenwich. */
export function formatIsoShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!parts) return iso;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(parts[3])} ${MONTHS[Number(parts[2]) - 1] ?? '?'} ${parts[1]}`;
}

/** Money, compact. Every figure in this module that can exceed four digits. */
export function Money({ value }: { value: number | null | undefined }) {
  return <>{formatCompactCurrency(value)}</>;
}

/**
 * The measure a tab leads with, against its previous window.
 *
 * `betterWhen` is required rather than defaulted, because getting it wrong is
 * silent: an abandon rate rising would be drawn as an improvement, and nobody
 * reading a green arrow questions it.
 */
export function DeltaStat({
  label,
  value,
  previous,
  format,
  betterWhen,
  foot,
}: {
  label: string;
  value: number | null;
  previous: number | null;
  format: (v: number | null) => string;
  betterWhen: 'higher' | 'lower';
  foot?: string;
}) {
  const delta = value !== null && previous !== null ? value - previous : null;
  /* Zero is neither: an unchanged measure gets no arrow rather than a
     misleading one. */
  const better = delta === null || delta === 0 ? null : betterWhen === 'higher' ? delta > 0 : delta < 0;

  return (
    <div className="stat">
      <span className="label">{label}</span>
      <div className="figure">{format(value)}</div>
      <span className="foot">
        {delta === null ? (
          foot ?? 'no prior period'
        ) : (
          <>
            <span aria-hidden="true">{delta === 0 ? '→' : better ? '▲' : '▼'}</span>{' '}
            {format(Math.abs(delta))} vs previous
          </>
        )}
      </span>
    </div>
  );
}

/**
 * What a tab shows when its register is empty.
 *
 * Its own component because the message matters: these registers ship empty by
 * design — that app's whole dataset was generated demo data and did not come
 * across (see migration 048) — so the first thing anybody sees on most of these
 * tabs is this. It has to say what the register is for and what to do, not
 * "no data".
 */
export function RegisterEmpty({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty admin-empty">
      <h3>{title}</h3>
      <div>{children}</div>
      {action && <div className="row wrap mt-4">{action}</div>}
    </div>
  );
}

/** The window picker every time-ranged tab carries. */
export function WindowPicker({
  value,
  onChange,
  options,
}: {
  value: number;
  onChange: (next: number) => void;
  options: { id: string; label: string }[];
}) {
  return (
    <Segmented
      label="Period"
      value={String(value)}
      options={options}
      onChange={(next) => onChange(Number(next))}
    />
  );
}

/** A band from a share of something, for tiles reporting a percentage against a
 *  convention rather than a stored target. */
export function shareBand(percent: number | null, good: number, warn: number): Band {
  if (percent === null) return 'neutral';
  if (percent >= good) return 'good';
  if (percent >= warn) return 'warning';
  return 'critical';
}
